import { BaseService } from '../../../core/services/base.service';

/** Gate state for the visitor. `enabled: false` means the tools are unlimited and no panel is shown. */
export type ToolGateStatus = {
  enabled: boolean;
  freeRemaining: number;
  grantRemaining: number;
  kofiUrl: string | null;
  minAmount: number | null;
  currency: string | null;
};

const DISABLED: ToolGateStatus = {
  enabled: false,
  freeRemaining: 0,
  grantRemaining: 0,
  kofiUrl: null,
  minAmount: null,
  currency: null,
};

/**
 * Thrown by a gated tool submit when the API answers 402 KOFI_REQUIRED.
 * Carries the Ko-fi page and the minimum donation so the panel can render them.
 */
export class KofiRequiredError extends Error {
  readonly kofiUrl: string;
  readonly minAmount: number;
  readonly currency: string;

  constructor(
    message: string,
    kofiUrl: string,
    minAmount: number,
    currency: string
  ) {
    super(message);
    Object.setPrototypeOf(this, KofiRequiredError.prototype);
    this.name = 'KofiRequiredError';
    this.kofiUrl = kofiUrl;
    this.minAmount = minAmount;
    this.currency = currency;
  }
}

export type RedeemErrorCode =
  | 'REDEEM_INVALID'
  | 'REDEEM_EXPIRED'
  | 'REDEEM_USED'
  | 'REDEEM_TOO_MANY_ATTEMPTS';

/** Thrown when a redeem code is rejected, with the API's reason code. */
export class RedeemError extends Error {
  readonly code: RedeemErrorCode;

  constructor(code: RedeemErrorCode, message: string) {
    super(message);
    Object.setPrototypeOf(this, RedeemError.prototype);
    this.name = 'RedeemError';
    this.code = code;
  }
}

/** Loads the gate state. Any failure is reported as disabled, so the tools keep working. */
export async function fetchToolGateStatus(): Promise<ToolGateStatus> {
  try {
    const response = await fetch(
      BaseService.BuildUrl('/public/support/kofi/status'),
      { credentials: 'include' }
    );
    return response.ok ? ((await response.json()) as ToolGateStatus) : DISABLED;
  } catch {
    return DISABLED;
  }
}

/** Redeems a supporter code and returns the visitor's updated allowance. */
export async function redeemKofiCode(code: string): Promise<ToolGateStatus> {
  const response = await fetch(
    BaseService.BuildUrl('/public/support/kofi/redeem'),
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: code.trim() }),
    }
  );
  if (response.ok) {
    return (await response.json()) as ToolGateStatus;
  }
  const body = await response.json().catch(() => ({}));
  throw new RedeemError(
    body.code ?? 'REDEEM_INVALID',
    body.message ?? "We couldn't check that code."
  );
}

/** Throws KofiRequiredError when a gated submit was refused for lack of runs. */
export async function throwIfKofiRequired(response: Response): Promise<void> {
  if (response.status !== 402) {
    return;
  }
  const body = await response.json().catch(() => ({}));
  throw new KofiRequiredError(
    body.message ?? "You've used your free resume checks.",
    body.kofiUrl ?? '',
    body.minAmount ?? 5,
    body.currency ?? 'USD'
  );
}
